import { useState, useEffect, useCallback, useMemo } from 'react';
import { useBuildingsData } from '../../Helpers/BuildingSummariesContext';
import {
    BuildingCheckWithTemplate,
    loadAllBuildingChecks,
    getDaysUntilDue
} from '../../Pages/BuildingChecks/buildingChecksUtils';
import { formatDateOnlyForStorage } from '../../Helpers/DateUtils';
import { buildingChecksApi } from '../../api/buildingChecksApi';

interface UseBuildingChecksReturn {
    buildingChecks: BuildingCheckWithTemplate[];
    snoozedBuildingChecks: BuildingCheckWithTemplate[];  // Snoozed items (snoozedUntil > today)
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
    snoozeBuildingCheck: (buildingId: string, checkKey: string, snoozeDate: string) => Promise<void>;
    unsnoozeBuildingCheck: (buildingId: string, checkKey: string) => Promise<void>;
}

/**
 * Hook to fetch all upcoming building checks across all buildings
 * Sorted by due date (soonest first)
 */
export const useBuildingChecks = (): UseBuildingChecksReturn => {
    const { buildings } = useBuildingsData();
    const [buildingChecks, setBuildingChecks] = useState<BuildingCheckWithTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadChecks = useCallback(async () => {
        if (!buildings || buildings.length === 0) {
            setBuildingChecks([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const buildingsForChecks = buildings.map(building => ({
                key: building.key,
                name: building.name || 'Namnlös byggnad'
            }));

            const checks = await loadAllBuildingChecks(buildingsForChecks);

            // Sort by due date (soonest first, then overdue)
            checks.sort((a, b) => {
                const daysA = getDaysUntilDue(a.nextDueDate);
                const daysB = getDaysUntilDue(b.nextDueDate);
                return daysA - daysB;
            });

            setBuildingChecks(checks);
        } catch (err) {
            console.error('Error loading building checks:', err);
            setError('Kunde inte ladda kontroller');
        } finally {
            setLoading(false);
        }
    }, [buildings]);

    useEffect(() => {
        loadChecks();
    }, [loadChecks]);

    // Separate active and snoozed building checks
    const { activeChecks, snoozedChecks } = useMemo(() => {
        const today = formatDateOnlyForStorage(new Date());

        const isSnoozed = (check: BuildingCheckWithTemplate) =>
            check.snoozedUntil && check.snoozedUntil > today;

        const active: BuildingCheckWithTemplate[] = [];
        const snoozed: BuildingCheckWithTemplate[] = [];

        for (const check of buildingChecks) {
            if (isSnoozed(check)) {
                snoozed.push(check);
            } else {
                active.push(check);
            }
        }

        return { activeChecks: active, snoozedChecks: snoozed };
    }, [buildingChecks]);

    // Snooze a building check
    const snoozeBuildingCheck = useCallback(async (
        buildingId: string,
        checkKey: string,
        snoozeDate: string
    ): Promise<void> => {
        try {
            await buildingChecksApi.snoozeBuildingCheck(buildingId, checkKey, snoozeDate);
            await loadChecks();
        } catch (err: unknown) {
            console.error('Error snoozing building check:', err);
            throw err;
        }
    }, [loadChecks]);

    // Unsnooze a building check
    const unsnoozeBuildingCheck = useCallback(async (
        buildingId: string,
        checkKey: string
    ): Promise<void> => {
        try {
            await buildingChecksApi.unsnoozeBuildingCheck(buildingId, checkKey);
            await loadChecks();
        } catch (err: unknown) {
            console.error('Error unsnoozing building check:', err);
            throw err;
        }
    }, [loadChecks]);

    return {
        buildingChecks: activeChecks,
        snoozedBuildingChecks: snoozedChecks,
        loading,
        error,
        refresh: loadChecks,
        snoozeBuildingCheck,
        unsnoozeBuildingCheck
    };
};
